/**
 * Parse ThinkOrSwim (TOS) order entry strings
 *
 * Example format:
 * "SELL -1 IRON CONDOR SBUX 100 30 JAN 26 103/112/92/82 CALL/PUT @2.65 LMT"
 *
 * Format breakdown:
 * - SELL/-1: Direction and quantity (SELL with negative = selling the spread)
 * - IRON CONDOR: Strategy name
 * - SBUX: Underlying symbol
 * - 100: Standard option multiplier
 * - 30 JAN 26: Expiration date (DD MMM YY)
 * - 103/112/92/82: Strikes (for iron condor: short call/long call/short put/long put)
 * - CALL/PUT: Option types
 * - @2.65: Limit price
 * - LMT: Order type
 */

import type { StrategyType } from '@steward/shared';

export interface ParsedLeg {
  optionType: 'call' | 'put';
  action: 'BTO' | 'STO';
  strikePrice: number;
  quantity: number;
  pricePerContract: number;
}

export interface ParsedTosOrder {
  underlyingSymbol: string;
  strategyType: StrategyType;
  expirationDate: string; // YYYY-MM-DD format
  quantity: number;
  totalPremium: number;
  legs: ParsedLeg[];
}

// Month name to number mapping
const monthMap: Record<string, string> = {
  'JAN': '01', 'FEB': '02', 'MAR': '03', 'APR': '04',
  'MAY': '05', 'JUN': '06', 'JUL': '07', 'AUG': '08',
  'SEP': '09', 'OCT': '10', 'NOV': '11', 'DEC': '12',
};

// Strategy name mapping from TOS to our internal types
const strategyMap: Record<string, StrategyType> = {
  'IRON CONDOR': 'iron_condor',
  'IRON BUTTERFLY': 'iron_butterfly',
  'VERTICAL': 'vertical_spread',
  'VERT': 'vertical_spread',
  'STRANGLE': 'strangle',
  'STRADDLE': 'straddle',
  'CALENDAR': 'calendar_spread',
  'DIAGONAL': 'diagonal_spread',
  'COVERED': 'covered_call',
  'SINGLE': 'custom',
};

/**
 * Parse a TOS order string into structured data
 */
export function parseTosOrder(orderString: string): ParsedTosOrder | null {
  try {
    // Normalize whitespace
    const str = orderString.trim().toUpperCase();

    // Extract direction and quantity: "SELL -1" or "BUY +2"
    const directionMatch = str.match(/^(BUY|SELL)\s+([+-]?\d+)/);
    if (!directionMatch) {
      console.log('Failed to match direction/quantity');
      return null;
    }

    const direction = directionMatch[1]; // BUY or SELL
    const quantity = Math.abs(parseInt(directionMatch[2], 10));
    const isSelling = direction === 'SELL';

    // Find strategy type
    let strategyType: StrategyType = 'custom';
    let strategyEndIndex = directionMatch[0].length;

    for (const [tosName, internalType] of Object.entries(strategyMap)) {
      const strategyIndex = str.indexOf(tosName, strategyEndIndex);
      if (strategyIndex !== -1 && strategyIndex < strategyEndIndex + 20) {
        strategyType = internalType;
        strategyEndIndex = strategyIndex + tosName.length;
        break;
      }
    }

    // Extract underlying symbol (word after strategy, before "100")
    const afterStrategy = str.substring(strategyEndIndex).trim();
    const symbolMatch = afterStrategy.match(/^([A-Z]+)\s+100/);
    if (!symbolMatch) {
      console.log('Failed to match underlying symbol');
      return null;
    }
    const underlyingSymbol = symbolMatch[1];

    // Extract expiration date: "30 JAN 26" pattern
    const dateMatch = str.match(/(\d{1,2})\s+(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)\s+(\d{2})/);
    if (!dateMatch) {
      console.log('Failed to match expiration date');
      return null;
    }

    const day = dateMatch[1].padStart(2, '0');
    const month = monthMap[dateMatch[2]];
    const year = `20${dateMatch[3]}`;
    const expirationDate = `${year}-${month}-${day}`;

    // Extract strikes: "103/112/92/82" pattern
    const strikesMatch = str.match(/(\d+(?:\.\d+)?(?:\/\d+(?:\.\d+)?)+)/);
    if (!strikesMatch) {
      console.log('Failed to match strikes');
      return null;
    }
    const strikes = strikesMatch[1].split('/').map(s => parseFloat(s));

    // Extract limit price: "@2.65" pattern
    const priceMatch = str.match(/@(\d+(?:\.\d+)?)/);
    const totalPremium = priceMatch ? parseFloat(priceMatch[1]) : 0;

    // Build legs based on strategy type
    const legs = buildLegs(strategyType, strikes, isSelling, quantity, totalPremium);

    if (legs.length === 0) {
      console.log('Failed to build legs');
      return null;
    }

    return {
      underlyingSymbol,
      strategyType,
      expirationDate,
      quantity,
      totalPremium,
      legs,
    };
  } catch (error) {
    console.error('Error parsing TOS order:', error);
    return null;
  }
}

/**
 * Build legs array based on strategy type and strikes
 *
 * For iron condor with strikes 103/112/92/82 (short call/long call/short put/long put):
 * When SELLING an iron condor (credit):
 * - STO call at 103 (short call)
 * - BTO call at 112 (long call wing)
 * - STO put at 92 (short put)
 * - BTO put at 82 (long put wing)
 */
function buildLegs(
  strategyType: StrategyType,
  strikes: number[],
  isSelling: boolean,
  quantity: number,
  _totalPremium: number // Unused for now - individual leg prices not available from TOS string
): ParsedLeg[] {
  const legs: ParsedLeg[] = [];

  switch (strategyType) {
    case 'iron_condor':
      // TOS format: short call / long call / short put / long put
      // Our form format: [put BTO, put STO, call STO, call BTO]
      if (strikes.length >= 4) {
        const [shortCall, longCall, shortPut, longPut] = strikes;

        // Put BTO (long put wing) - buying protection
        legs.push({
          optionType: 'put',
          action: isSelling ? 'BTO' : 'STO',
          strikePrice: longPut,
          quantity,
          pricePerContract: 0,
        });

        // Put STO (short put) - collecting premium
        legs.push({
          optionType: 'put',
          action: isSelling ? 'STO' : 'BTO',
          strikePrice: shortPut,
          quantity,
          pricePerContract: 0,
        });

        // Call STO (short call) - collecting premium
        legs.push({
          optionType: 'call',
          action: isSelling ? 'STO' : 'BTO',
          strikePrice: shortCall,
          quantity,
          pricePerContract: 0,
        });

        // Call BTO (long call wing) - buying protection
        legs.push({
          optionType: 'call',
          action: isSelling ? 'BTO' : 'STO',
          strikePrice: longCall,
          quantity,
          pricePerContract: 0,
        });
      }
      break;

    case 'iron_butterfly':
      // Similar structure to iron condor but middle strikes are same
      if (strikes.length >= 4) {
        const [shortCall, longCall, shortPut, longPut] = strikes;

        legs.push({
          optionType: 'put',
          action: isSelling ? 'BTO' : 'STO',
          strikePrice: longPut,
          quantity,
          pricePerContract: 0,
        });

        legs.push({
          optionType: 'put',
          action: isSelling ? 'STO' : 'BTO',
          strikePrice: shortPut,
          quantity,
          pricePerContract: 0,
        });

        legs.push({
          optionType: 'call',
          action: isSelling ? 'STO' : 'BTO',
          strikePrice: shortCall,
          quantity,
          pricePerContract: 0,
        });

        legs.push({
          optionType: 'call',
          action: isSelling ? 'BTO' : 'STO',
          strikePrice: longCall,
          quantity,
          pricePerContract: 0,
        });
      }
      break;

    case 'vertical_spread':
      // Two strikes: short/long
      if (strikes.length >= 2) {
        const [strike1, strike2] = strikes;
        // Determine if it's a put or call spread based on the order string context
        // Default to put spread

        legs.push({
          optionType: 'put',
          action: isSelling ? 'STO' : 'BTO',
          strikePrice: Math.max(strike1, strike2),
          quantity,
          pricePerContract: 0,
        });

        legs.push({
          optionType: 'put',
          action: isSelling ? 'BTO' : 'STO',
          strikePrice: Math.min(strike1, strike2),
          quantity,
          pricePerContract: 0,
        });
      }
      break;

    case 'strangle':
      // Two strikes: put strike / call strike
      if (strikes.length >= 2) {
        const [putStrike, callStrike] = strikes.sort((a, b) => a - b);

        legs.push({
          optionType: 'put',
          action: isSelling ? 'STO' : 'BTO',
          strikePrice: putStrike,
          quantity,
          pricePerContract: 0,
        });

        legs.push({
          optionType: 'call',
          action: isSelling ? 'STO' : 'BTO',
          strikePrice: callStrike,
          quantity,
          pricePerContract: 0,
        });
      }
      break;

    case 'straddle':
      // One strike for both
      if (strikes.length >= 1) {
        const strike = strikes[0];

        legs.push({
          optionType: 'put',
          action: isSelling ? 'STO' : 'BTO',
          strikePrice: strike,
          quantity,
          pricePerContract: 0,
        });

        legs.push({
          optionType: 'call',
          action: isSelling ? 'STO' : 'BTO',
          strikePrice: strike,
          quantity,
          pricePerContract: 0,
        });
      }
      break;

    default:
      // For custom/single leg trades, create based on strikes
      for (const strike of strikes) {
        legs.push({
          optionType: 'call',
          action: isSelling ? 'STO' : 'BTO',
          strikePrice: strike,
          quantity,
          pricePerContract: 0,
        });
      }
  }

  return legs;
}

/**
 * Validate if a string looks like a TOS order string
 */
export function isTosOrderString(str: string): boolean {
  const upper = str.trim().toUpperCase();
  return /^(BUY|SELL)\s+[+-]?\d+/.test(upper) && /@[\d.]+/.test(upper);
}

/**
 * Generate a TOS order string from form data
 */
export interface GenerateTosOrderInput {
  underlyingSymbol: string;
  strategyType: StrategyType;
  legs: Array<{
    optionType: 'call' | 'put';
    action: 'BTO' | 'STO';
    strikePrice: number;
    expirationDate: string; // YYYY-MM-DD
    quantity: number;
    pricePerContract: number;
  }>;
}

// Reverse month map for generation
const monthNames: Record<string, string> = {
  '01': 'JAN', '02': 'FEB', '03': 'MAR', '04': 'APR',
  '05': 'MAY', '06': 'JUN', '07': 'JUL', '08': 'AUG',
  '09': 'SEP', '10': 'OCT', '11': 'NOV', '12': 'DEC',
};

// Strategy name mapping to TOS format
const strategyToTos: Record<StrategyType, string> = {
  'iron_condor': 'IRON CONDOR',
  'iron_butterfly': 'IRON BUTTERFLY',
  'vertical_spread': 'VERTICAL',
  'strangle': 'STRANGLE',
  'straddle': 'STRADDLE',
  'calendar_spread': 'CALENDAR',
  'diagonal_spread': 'DIAGONAL',
  'covered_call': 'COVERED',
  'cash_secured_put': 'SINGLE',
  'single_call': 'SINGLE',
  'single_put': 'SINGLE',
  'custom': 'CUSTOM',
};

export function generateTosOrderString(input: GenerateTosOrderInput): string | null {
  const { underlyingSymbol, strategyType, legs } = input;

  if (!underlyingSymbol || legs.length === 0) {
    return null;
  }

  // Check if all legs have valid data
  const validLegs = legs.filter(leg =>
    leg.strikePrice > 0 && leg.expirationDate && leg.quantity > 0
  );

  if (validLegs.length === 0) {
    return null;
  }

  // Calculate net premium to determine BUY/SELL direction
  const netPremium = legs.reduce((sum, leg) => {
    const value = (leg.pricePerContract || 0) * (leg.quantity || 0);
    return sum + (leg.action === 'STO' ? value : -value);
  }, 0);

  // Credit spread = SELL, Debit spread = BUY
  const isSelling = netPremium >= 0;
  const direction = isSelling ? 'SELL' : 'BUY';
  const quantity = legs[0]?.quantity || 1;
  const signedQty = isSelling ? `-${quantity}` : `+${quantity}`;

  // Get strategy name
  const strategyName = strategyToTos[strategyType] || 'CUSTOM';

  // Parse expiration date (use first leg's date)
  const expDate = legs[0]?.expirationDate;
  if (!expDate) {
    return null;
  }

  const [year, month, day] = expDate.split('-');
  const monthName = monthNames[month];
  const shortYear = year.slice(2);
  const dateStr = `${parseInt(day, 10)} ${monthName} ${shortYear}`;

  // Build strikes string based on strategy
  const strikesStr = buildStrikesString(strategyType, legs, isSelling);
  if (!strikesStr) {
    return null;
  }

  // Determine option types involved
  const hasCall = legs.some(leg => leg.optionType === 'call');
  const hasPut = legs.some(leg => leg.optionType === 'put');
  let optionTypesStr = '';
  if (hasCall && hasPut) {
    optionTypesStr = 'CALL/PUT';
  } else if (hasCall) {
    optionTypesStr = 'CALL';
  } else {
    optionTypesStr = 'PUT';
  }

  // Calculate total spread price (absolute value)
  const totalPrice = Math.abs(netPremium).toFixed(2);

  return `${direction} ${signedQty} ${strategyName} ${underlyingSymbol.toUpperCase()} 100 ${dateStr} ${strikesStr} ${optionTypesStr} @${totalPrice} LMT`;
}

/**
 * Build strikes string based on strategy type
 * For iron condor: short call / long call / short put / long put
 */
function buildStrikesString(
  strategyType: StrategyType,
  legs: GenerateTosOrderInput['legs'],
  isSelling: boolean
): string | null {
  switch (strategyType) {
    case 'iron_condor':
    case 'iron_butterfly': {
      // Find each leg by type and action
      const shortCall = legs.find(l => l.optionType === 'call' && l.action === (isSelling ? 'STO' : 'BTO'));
      const longCall = legs.find(l => l.optionType === 'call' && l.action === (isSelling ? 'BTO' : 'STO'));
      const shortPut = legs.find(l => l.optionType === 'put' && l.action === (isSelling ? 'STO' : 'BTO'));
      const longPut = legs.find(l => l.optionType === 'put' && l.action === (isSelling ? 'BTO' : 'STO'));

      if (!shortCall || !longCall || !shortPut || !longPut) {
        // Fall back to simple ordering
        const strikes = legs.map(l => l.strikePrice).filter(s => s > 0);
        return strikes.join('/');
      }

      // TOS format: short call / long call / short put / long put
      return `${shortCall.strikePrice}/${longCall.strikePrice}/${shortPut.strikePrice}/${longPut.strikePrice}`;
    }

    case 'vertical_spread': {
      // Two strikes: higher strike / lower strike for put spread
      const strikes = legs.map(l => l.strikePrice).filter(s => s > 0).sort((a, b) => b - a);
      return strikes.join('/');
    }

    case 'strangle': {
      // Put strike / Call strike
      const putLeg = legs.find(l => l.optionType === 'put');
      const callLeg = legs.find(l => l.optionType === 'call');
      if (putLeg && callLeg) {
        return `${putLeg.strikePrice}/${callLeg.strikePrice}`;
      }
      return legs.map(l => l.strikePrice).join('/');
    }

    case 'straddle': {
      // Single strike
      const strike = legs[0]?.strikePrice;
      return strike ? String(strike) : null;
    }

    default: {
      // Generic: just list all strikes
      const strikes = legs.map(l => l.strikePrice).filter(s => s > 0);
      return strikes.length > 0 ? strikes.join('/') : null;
    }
  }
}
